'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { motion, useReducedMotion } from 'framer-motion';
import { X, Cookie, Settings } from 'lucide-react';
import { useTheme } from '@/components/ThemeProvider';
import { themedTint } from '@/components/storefront/booking/_shared/tokens';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface TenantForCookies {
  id: string;
  name: string;
  primary_color?: string | null;
  secondary_color?: string | null;
  text_color?: string | null;
  background_color?: string | null;
}

interface StorefrontCookieBannerProps {
  tenant: TenantForCookies;
}

export default function StorefrontCookieBanner({ tenant }: StorefrontCookieBannerProps) {
  const t = useTranslations('components.storefrontCookieBanner');
  const theme = useTheme();
  const reduce = useReducedMotion();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  // Theming — resolved from the storefront's --sf-* vars + ThemeProvider so the
  // banner inherits light/dark surface, neutral ink and the tenant accent
  // automatically (it lives inside the [data-sf-theme] + ThemeProvider subtree).
  const accent = theme.primaryColor; // var(--primary-color, <tenant default>)
  const onAccent = theme.onAccent; // contrast-safe ink on the accent fill
  const surface = 'var(--sf-card, #ffffff)'; // dark-safe card (dark sets --sf-card)
  const ink = 'var(--sf-text, #374151)'; // neutral body foreground
  const muted = 'var(--sf-muted, #6b7280)'; // quiet secondary text
  const hairline = 'var(--sf-accent-border, rgba(15,23,42,0.10))'; // subtle branded border

  useEffect(() => {
    // Check for existing consent for this specific tenant
    const cookieConsent = localStorage.getItem(`cookie-consent-${tenant.id}`);
    if (!cookieConsent) {
      // Small delay to ensure smooth page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load existing preferences
      try {
        const savedPreferences = JSON.parse(cookieConsent);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot mount sync / external store subscription
        setPreferences(savedPreferences);
      } catch (error) {
        // Warning handled: 'Error parsing saved cookie preferences:', error);
      }
    }
  }, [tenant.id]);

  const acceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allPreferences);
    localStorage.setItem(`cookie-consent-${tenant.id}`, JSON.stringify(allPreferences));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(necessaryOnly);
    localStorage.setItem(`cookie-consent-${tenant.id}`, JSON.stringify(necessaryOnly));
    setShowBanner(false);
  };

  const savePreferences = () => {
    localStorage.setItem(`cookie-consent-${tenant.id}`, JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences((prev) => ({ ...prev, [type]: value }));
  };

  if (!showBanner) return null;

  // Shared button primitives — Tailwind hover/focus vocabulary, no inline JS hacks.
  const primaryBtn =
    'inline-flex h-10 items-center justify-center rounded-full px-4 text-[13px] font-semibold shadow-sm transition-[filter,box-shadow,transform] duration-200 hover:-translate-y-px hover:shadow-md hover:brightness-[1.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:translate-y-0';
  const quietBtn =
    'inline-flex h-10 items-center justify-center rounded-full border px-4 text-[13px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)]';
  const ghostBtn =
    'inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-3 text-[13px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)]';

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 sm:p-4">
      <motion.div
        role="dialog"
        aria-label={t('title')}
        aria-live="polite"
        initial={reduce ? false : { opacity: 0, y: 20 }}
        animate={reduce ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`pointer-events-auto relative w-full rounded-2xl border shadow-xl backdrop-blur-sm ${
          showSettings ? 'max-w-lg' : 'max-w-3xl'
        }`}
        style={
          {
            backgroundColor: surface,
            color: ink,
            borderColor: hairline,
            ['--tw-ring-color']: themedTint(accent, 45),
            ['--tw-ring-offset-color']: surface,
          } as React.CSSProperties
        }
      >
        {/* Dismiss — subtle, tucked in the trailing corner (toast-style). */}
        <button
          onClick={() => setShowBanner(false)}
          aria-label={t('acceptNecessary')}
          className="absolute end-2 top-2 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,currentColor_8%,transparent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ color: muted }}
        >
          <X className="h-4 w-4" />
        </button>

        {!showSettings ? (
          /* ---------- Compact bar (default) — slim single-row on desktop ---------- */
          <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4 sm:py-2.5 sm:pe-3 sm:ps-4">
            <div className="flex items-center gap-2.5 pe-8 sm:min-w-0 sm:flex-1 sm:pe-2">
              <span
                className="flex h-7 w-7 flex-none items-center justify-center rounded-lg"
                style={{ backgroundColor: themedTint(accent, 12) }}
                aria-hidden="true"
              >
                <Cookie className="h-4 w-4" style={{ color: accent }} />
              </span>
              <p
                className="line-clamp-2 min-w-0 text-[13px] leading-snug"
                style={{ color: ink }}
              >
                {t('introText', { tenantName: tenant.name })}
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-none sm:flex-row sm:items-center">
              <button
                onClick={() => setShowSettings(true)}
                className={`${ghostBtn} order-last sm:order-none`}
                style={{ color: muted }}
              >
                <Settings className="h-4 w-4" />
                {t('customize')}
              </button>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
                <button
                  onClick={acceptNecessary}
                  className={quietBtn}
                  style={{ borderColor: hairline, color: ink }}
                >
                  {t('acceptNecessary')}
                </button>
                <button
                  onClick={acceptAll}
                  className={primaryBtn}
                  style={{ backgroundColor: accent, color: onAccent }}
                >
                  {t('acceptAll')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ---------- Granular settings (opt-in expansion) ---------- */
          <div className="p-4 sm:p-5">
            <div className="mb-4 flex items-center gap-2.5 pe-8">
              <span
                className="flex h-8 w-8 flex-none items-center justify-center rounded-lg"
                style={{ backgroundColor: themedTint(accent, 12) }}
                aria-hidden="true"
              >
                <Cookie className="h-4 w-4" style={{ color: accent }} />
              </span>
              <div>
                <h3 className="text-base font-semibold leading-tight" style={{ color: accent }}>
                  {t('title')}
                </h3>
                <p className="text-xs" style={{ color: muted }}>
                  {tenant.name}
                </p>
              </div>
            </div>

            <div className="mb-5 space-y-2.5">
              {/* Necessary */}
              <div
                className="flex items-center justify-between gap-3 rounded-xl border p-3.5"
                style={{ borderColor: hairline }}
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h4 className="text-sm font-semibold" style={{ color: ink }}>
                      {t('necessaryTitle')}
                    </h4>
                    <span
                      className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{ backgroundColor: themedTint(accent, 16), color: accent }}
                    >
                      {t('required')}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: muted }}>
                    {t('necessaryDesc')}
                  </p>
                </div>
                <div className="flex flex-none items-center gap-2">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="h-5 w-5 rounded"
                    style={{ accentColor: accent }}
                  />
                  <span className="text-xs font-medium" style={{ color: muted }}>
                    {t('alwaysActive')}
                  </span>
                </div>
              </div>

              {/* Analytics */}
              <label
                className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-3.5 transition-colors duration-200"
                style={{
                  borderColor: preferences.analytics ? themedTint(accent, 40) : hairline,
                  backgroundColor: preferences.analytics ? themedTint(accent, 8) : 'transparent',
                }}
              >
                <div className="flex-1">
                  <h4 className="mb-1 text-sm font-semibold" style={{ color: ink }}>
                    {t('analyticsTitle')}
                  </h4>
                  <p className="text-xs leading-relaxed" style={{ color: muted }}>
                    {t('analyticsDesc')}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                  className="h-5 w-5 flex-none rounded"
                  style={{ accentColor: accent }}
                />
              </label>

              {/* Marketing */}
              <label
                className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-3.5 transition-colors duration-200"
                style={{
                  borderColor: preferences.marketing ? themedTint(accent, 40) : hairline,
                  backgroundColor: preferences.marketing ? themedTint(accent, 8) : 'transparent',
                }}
              >
                <div className="flex-1">
                  <h4 className="mb-1 text-sm font-semibold" style={{ color: ink }}>
                    {t('marketingTitle')}
                  </h4>
                  <p className="text-xs leading-relaxed" style={{ color: muted }}>
                    {t('marketingDesc')}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                  className="h-5 w-5 flex-none rounded"
                  style={{ accentColor: accent }}
                />
              </label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-center">
              <button
                onClick={savePreferences}
                className={`${primaryBtn} sm:flex-1`}
                style={{ backgroundColor: accent, color: onAccent }}
              >
                {t('saveSettings')}
              </button>
              <button
                onClick={acceptNecessary}
                className={quietBtn}
                style={{ borderColor: hairline, color: ink }}
              >
                {t('acceptNecessary')}
              </button>
            </div>

            <div
              className="mt-4 border-t pt-3"
              style={{ borderColor: hairline }}
            >
              <p className="text-center text-[11px] leading-relaxed" style={{ color: muted }}>
                {t('legalNotice', { tenantName: tenant.name })}
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
